import { useQuery, UseQueryResult } from '@tanstack/react-query'

import Api from 'services/Api'
import { hashTable } from 'services/Utilities'

import { CACHE_TIME, RESSOURCES_ENDPOINTS, STALE_TIME } from 'Api/constants'
import { ContactTemplate } from 'Api/types'
import { queryClient } from 'services/QueryClient'

interface ContactTemplateResult {
  templates: ContactTemplate[]
}

const contactTemplatesQuery = async () => {
  const contactTemplates: ContactTemplateResult = await Api.get(RESSOURCES_ENDPOINTS.contactTemplates)
  return contactTemplatesTransformer(contactTemplates.templates)
}

const contactTemplatesTransformer = (contactTemplates: ContactTemplate[]) =>
  ({
    contactTemplates,
    contactTemplatesIds: contactTemplates?.map(({ _id }) => _id),
    contactTemplatesById: contactTemplates?.length ? hashTable(contactTemplates) : undefined,
    count: contactTemplates.length ?? 0
  } as const)

type ContactTemplatesData = ReturnType<typeof contactTemplatesTransformer>

const getQueryConfig = () =>
  ({
    queryKey: ['contactTemplates'],
    queryFn: () => contactTemplatesQuery(),
    cacheTime: CACHE_TIME,
    staleTime: STALE_TIME
  } as const)

type Select<T> = (data: ContactTemplatesData) => T

const useContactTemplates = <T = ContactTemplatesData>(select?: Select<T>) => {
  const config = getQueryConfig()
  return useQuery({ ...config, select })
}

const useContactTemplateById = (id: string): UseQueryResult<ContactTemplate | undefined> => {
  return useContactTemplates(data => data.contactTemplatesById?.[id])
}

const prefetchContactTemplates = async () => queryClient.prefetchQuery({ ...getQueryConfig() })
const invalidateContactTemplates = async () => queryClient.invalidateQueries(getQueryConfig().queryKey)
const getContactTemplatesQueryCache = () => queryClient.getQueryData(getQueryConfig().queryKey)

export { useContactTemplates, useContactTemplateById, prefetchContactTemplates, invalidateContactTemplates, getContactTemplatesQueryCache }
